import { useEffect, useRef } from 'react';
import { captureTracking, mintEventMeta, installParentContextListener } from '@/lib/tracking';

/**
 * useTracking — captures attribution on mount, exposes:
 *   - get()           → returns the current tracking blob (without re-capture)
 *   - withEventMeta() → returns { ...tracking, event_id, original_event_time }
 *                       for use as the cookies_data payload at submission time
 *
 * Call captureTracking() once on mount so by the time user submits, we already
 * have everything stored.
 */
export function useTracking() {
  const dataRef = useRef(null);

  useEffect(() => {
    dataRef.current = captureTracking();
    // Listen for parent-page UTM forwarding (postMessage). Iframe-only:
    // a noop when the form is loaded directly. The listener writes new
    // UTMs to storage, so the next captureTracking() call (or withEventMeta
    // at submit time) picks them up.
    const cleanup = installParentContextListener();
    return cleanup;
  }, []);

  return {
    get() {
      // Re-read storage so parent UTMs that arrived after mount are reflected.
      return captureTracking();
    },
    withEventMeta() {
      const base = captureTracking();
      return { ...base, ...mintEventMeta() };
    },
  };
}
